// create-wp-reactor — scaffolder de repo client mince.
//
// Émet un monorepo client « 2 mondes » depuis un template BUNDLÉ (npx-friendly :
// aucun accès au monorepo framework au runtime) :
//   • apps/webapp     ← coque (starter synchronisé dans templates/, voir
//                       scripts/sync-template.mjs)
//   • apps/wordpress  ← thème ENFANT
//   • .github, docker, wp-reactor.config.json, workspace root
//
// `scaffold` génère un nouveau repo ; `update` rafraîchit un repo existant sur
// deux axes — les fichiers d'INFRA (compose dev, docs, Dockerfiles, workflows,
// skills agent) et les versions des deps `@wp-reactor/*` — jamais la coque src/
// ni le branding.

import {
	existsSync,
	mkdirSync,
	readdirSync,
	readFileSync,
	statSync,
	writeFileSync,
} from "node:fs"
import { basename, dirname, join, resolve } from "node:path"
import { fileURLToPath } from "node:url"
import {
	applyFeatureMarkers,
	isDisabledPath,
	OPTIONAL_FEATURES,
	stripFeatureDeps,
} from "./features"
import { PACKAGE_VERSIONS } from "./versions.generated"

const HERE = dirname(fileURLToPath(import.meta.url))
// src/ (dev via tsx) comme dist/ (build) sont à 1 niveau du package.
const TEMPLATES_DIR = resolve(HERE, "../templates/client")

// Fichiers d'INFRA re-poussables par `update` (le client ne les personnalise
// quasiment jamais). La coque src/, le branding (thème enfant), package.json,
// wp-reactor.config.json, .env.example, .gitignore/.npmrc en sont EXCLUS.
const INFRA_PATHS = [
	"docker-compose.dev.yml",
	"docs",
	// Skills agent du framework (gen-block…) : ils documentent le CLI, donc ils
	// suivent ses versions. `update` les re-pousse ; un skill propre au client
	// posé à côté n'est jamais touché (renderInto écrit, ne purge pas).
	".claude/skills",
	// Le DOSSIER, pas la liste des workflows : le template ne livre que des
	// workflows d'infra, et une liste fichier-par-fichier dérive (le reusable
	// vendorisé build-and-deploy-image.yml y a manqué). Un workflow propre au
	// client posé à côté n'est jamais touché (renderInto écrit, ne purge pas).
	".github/workflows",
	"apps/wordpress/Dockerfile",
	"apps/wordpress/docker-compose.yml",
	"apps/wordpress/docker",
	"apps/webapp/Dockerfile",
	"apps/webapp/scripts",
	// Garde-fou sur les `default` des schémas, documenté par le skill gen-block :
	// il suit les règles du CLI, donc ses versions.
	"scripts/check-block-schemas.mjs",
]

export interface ScaffoldOptions {
	/**
	 * Nom du projet (ex. "Client 2") → slugifié en `client-2` pour les noms de
	 * packages, dossiers, thème, projet Docker Compose et labels Traefik.
	 */
	projectName: string
	/** Dossier cible (créé). */
	targetDir: string
	/**
	 * Domaines optionnels activés (« commerce », « auth »). L'éditorial est le
	 * socle et n'en fait pas partie. Omis = tous actifs : le template complet
	 * reste la variante par défaut, et la sandbox de dogfood la construit.
	 */
	features?: readonly string[]
	/** Namespace des blocs gen-block. Défaut : projectName sans tirets. */
	namespace?: string
	/** Override du dossier template (tests). */
	templatesDir?: string
	/**
	 * Override appliqué à TOUTES les deps `@wp-reactor/*` (placeholder
	 * `__PKG_RANGE__`). Par défaut, chaque dep est résolue vers `^<version>` du
	 * package via le manifeste embarqué (`versions.generated.ts`). Le scaffold
	 * dogfood passe `workspace:*` ; `$WP_REACTOR_PKG_RANGE` sert d'override global.
	 */
	pkgRange?: string
}

export interface UpdateOptions {
	/** Racine du repo client existant. */
	targetDir: string
	/** Override du dossier template (tests). */
	templatesDir?: string
}

export interface ScaffoldResult {
	targetDir: string
	written: string[]
}

/** Une range `@wp-reactor/*` réécrite par `update` dans un package.json client. */
export interface DepBump {
	file: string
	dep: string
	from: string
	to: string
}

export interface UpdateResult extends ScaffoldResult {
	/** Ranges réécrites vers la version embarquée dans ce CLI. */
	bumps: DepBump[]
	/**
	 * Ranges laissées en place parce qu'elles sont DEVANT le manifeste embarqué.
	 * Le manifeste est figé au build de create-wp-reactor : un package publié
	 * après lui y est encore à sa version précédente, et l'écrire reculerait le
	 * client.
	 */
	kept: DepBump[]
	/** Deps `@wp-reactor/*` absentes du manifeste embarqué — laissées telles quelles. */
	unknownDeps: string[]
}

/**
 * Slug injecté dans `__PROJECT_NAME__`. Le token atterrit dans des identifiants
 * qui n'acceptent PAS d'espaces/majuscules/accents — `name:` du Docker Compose
 * (`[a-z0-9][a-z0-9_-]*`), noms de packages npm, dossier du thème enfant, labels
 * Traefik — donc on slugifie au lieu de recopier l'argument CLI tel quel.
 */
export function toProjectSlug(input: string): string {
	const slug = input
		.normalize("NFD")
		.replace(/\p{M}/gu, "")
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, "-")
		.replace(/^-+|-+$/g, "")
	if (!slug) {
		throw new Error(
			`Nom de projet inutilisable : "${input}" ne contient aucun caractère [a-z0-9].`,
		)
	}
	return slug
}

function titleCase(input: string): string {
	return input
		.split(/[-_\s]+/)
		.filter(Boolean)
		.map((w) => w[0].toUpperCase() + w.slice(1))
		.join(" ")
}

function applyTokens(input: string, tokens: Record<string, string>): string {
	let out = input
	for (const [key, value] of Object.entries(tokens)) {
		out = out.split(key).join(value)
	}
	return out
}

// Override global rétro-compat ; sinon résolution par package via le manifeste.
const PKG_RANGE_ENV_OVERRIDE = process.env.WP_REACTOR_PKG_RANGE

function tokensFor(
	projectName: string,
	namespace?: string,
): Record<string, string> {
	const slug = toProjectSlug(projectName)
	return {
		__PROJECT_NAME__: slug,
		__PROJECT_TITLE__: titleCase(projectName),
		// Namespace de bloc WP : `[a-z][a-z0-9-]*`, donc slugifié lui aussi.
		__BLOCK_NAMESPACE__: namespace
			? toProjectSlug(namespace)
			: slug.replace(/-/g, ""),
	}
}

/**
 * Range injectée pour une dep `@wp-reactor/*`. Override explicite (dogfood
 * `workspace:*`, ou `$WP_REACTOR_PKG_RANGE`), sinon `^<version publiée>` lue dans
 * le manifeste — chaque package suit sa propre version, plus de range partagée.
 */
function pkgRangeFor(depName: string, override?: string): string {
	if (override) return override
	const version = PACKAGE_VERSIONS[depName]
	if (!version) {
		throw new Error(
			`create-wp-reactor : version inconnue pour ${depName} (régénère src/versions.generated.ts).`,
		)
	}
	return `^${version}`
}

/** Réécrit les deps `@wp-reactor/*` (placeholder `__PKG_RANGE__`) d'un package.json. */
function rewritePkgRanges(content: string, override?: string): string {
	return content.replace(
		/"(@wp-reactor\/[^"]+)"(\s*:\s*)"__PKG_RANGE__"/g,
		(_m, name: string, sep: string) =>
			`"${name}"${sep}"${pkgRangeFor(name, override)}"`,
	)
}

// Fichiers stockés sous un autre nom dans le template, rétablis à l'écriture.
// Deux raisons distinctes :
//   • npm strippe `.gitignore` et `.npmrc` des tarballs publiés ;
//   • un `biome.json` sous `templates/` serait vu par Biome comme une seconde
//     configuration RACINE du monorepo framework, qu'il refuse — et l'exclure
//     via `files.includes` ne suffit pas, la découverte se fait avant.
const DOTFILES: Record<string, string> = {
	gitignore: ".gitignore",
	npmrc: ".npmrc",
	"biome.root.json": "biome.json",
}

/** Rend un fichier ou un dossier (récursif) en substituant les tokens (chemins + contenu). */
function renderInto(
	src: string,
	dest: string,
	tokens: Record<string, string>,
	written: string[],
	pkgOverride?: string,
	features?: { enabled: ReadonlySet<string>; rel: string },
): void {
	if (features && isDisabledPath(features.rel, features.enabled)) return

	if (statSync(src).isDirectory()) {
		mkdirSync(dest, { recursive: true })
		for (const entry of readdirSync(src)) {
			const destName = DOTFILES[entry] ?? applyTokens(entry, tokens)
			renderInto(
				join(src, entry),
				join(dest, destName),
				tokens,
				written,
				pkgOverride,
				features && {
					enabled: features.enabled,
					rel: features.rel ? `${features.rel}/${entry}` : entry,
				},
			)
		}
	} else {
		let content = applyTokens(readFileSync(src, "utf8"), tokens)
		if (features) content = applyFeatureMarkers(content, features.enabled)
		if (basename(dest) === "package.json") {
			content = rewritePkgRanges(content, pkgOverride)
			if (features) content = stripFeatureDeps(content, features.enabled)
		}
		mkdirSync(dirname(dest), { recursive: true })
		writeFileSync(dest, content)
		written.push(dest)
	}
}

export function scaffold(opts: ScaffoldOptions): ScaffoldResult {
	const templatesDir = opts.templatesDir ?? TEMPLATES_DIR
	const targetDir = resolve(opts.targetDir)
	const tokens = tokensFor(opts.projectName, opts.namespace)
	const pkgOverride = opts.pkgRange ?? PKG_RANGE_ENV_OVERRIDE
	const written: string[] = []

	const enabled = new Set<string>(opts.features ?? OPTIONAL_FEATURES)

	mkdirSync(targetDir, { recursive: true })
	renderInto(templatesDir, targetDir, tokens, written, pkgOverride, {
		enabled,
		rel: "",
	})

	return { targetDir, written }
}

/** Récupère le nom de projet + namespace depuis un repo client déjà généré. */
function recoverIdentity(targetDir: string): {
	projectName: string
	namespace?: string
} {
	const pkgPath = join(targetDir, "package.json")
	if (!existsSync(pkgPath)) {
		throw new Error(
			`Pas un repo client create-wp-reactor : ${pkgPath} introuvable.`,
		)
	}
	const name = JSON.parse(readFileSync(pkgPath, "utf8")).name ?? ""
	const match = /^@([^/]+)\/source$/.exec(name)
	if (!match) {
		throw new Error(
			`package.json.name attendu "@<projet>/source", trouvé "${name}".`,
		)
	}
	const projectName = match[1]
	const configPath = join(targetDir, "wp-reactor.config.json")
	const namespace = existsSync(configPath)
		? JSON.parse(readFileSync(configPath, "utf8")).namespace
		: undefined
	return { projectName, namespace }
}

const IGNORED_DIRS = new Set([
	"node_modules",
	".git",
	".turbo",
	"dist",
	".output",
	".vinxi",
])

const DEP_SECTIONS = [
	"dependencies",
	"devDependencies",
	"peerDependencies",
	"optionalDependencies",
]

// Ranges pointant vers un lien local (dogfood monorepo, `pnpm link`) : `update`
// ne les réécrit jamais, sinon il casserait le lien.
const LOCAL_RANGE_PREFIXES = ["workspace:", "link:", "file:", "portal:"]

function escapeRegExp(input: string): string {
	return input.replace(/[.*+?^${}()|[\]\\/]/g, "\\$&")
}

function collectPackageJsonFiles(dir: string, out: string[] = []): string[] {
	// withFileTypes → isDirectory() suit lstat, donc pas de descente dans un symlink.
	for (const entry of readdirSync(dir, { withFileTypes: true })) {
		if (entry.isDirectory()) {
			if (!IGNORED_DIRS.has(entry.name)) {
				collectPackageJsonFiles(join(dir, entry.name), out)
			}
		} else if (entry.name === "package.json") {
			out.push(join(dir, entry.name))
		}
	}
	return out
}

/** Noms de deps `@wp-reactor/*` déclarés dans les sections de deps d'un manifeste. */
function frameworkDepNames(pkg: unknown): string[] {
	if (typeof pkg !== "object" || pkg === null) return []
	const names = new Set<string>()
	for (const section of DEP_SECTIONS) {
		const deps = (pkg as Record<string, unknown>)[section]
		if (typeof deps !== "object" || deps === null) continue
		for (const name of Object.keys(deps)) {
			if (name.startsWith("@wp-reactor/")) names.add(name)
		}
	}
	return [...names]
}

/**
 * Réécrit les ranges `@wp-reactor/*` de tous les package.json du repo client vers
 * `^<version>` du manifeste embarqué. Remplacement chirurgical (regex sur la seule
 * chaîne de version) : l'indentation, l'ordre des clés et le reste du fichier sont
 * préservés, donc le `git diff` ne montre que les bumps.
 */
/**
 * Compare deux versions `x.y.z`. Rend un nombre négatif si `a` précède `b`,
 * positif s'il la suit, 0 à égalité — `NaN` si l'une n'est pas analysable.
 *
 * Pas de dépendance semver pour ça : le manifeste ne contient que des versions
 * publiées, donc `x.y.z` sans préversion.
 */
function compareVersions(a: string, b: string): number {
	const parse = (value: string) => {
		const match = /(\d+)\.(\d+)\.(\d+)/.exec(value)
		return match ? match.slice(1, 4).map(Number) : null
	}
	const left = parse(a)
	const right = parse(b)
	if (!left || !right) return Number.NaN
	for (let i = 0; i < 3; i += 1) {
		if (left[i] !== right[i]) return left[i] - right[i]
	}
	return 0
}

function bumpFrameworkDeps(targetDir: string): {
	bumps: DepBump[]
	kept: DepBump[]
	unknownDeps: string[]
} {
	const bumps: DepBump[] = []
	const kept: DepBump[] = []
	const unknownDeps = new Set<string>()

	for (const file of collectPackageJsonFiles(targetDir)) {
		const original = readFileSync(file, "utf8")
		let pkg: unknown
		try {
			pkg = JSON.parse(original)
		} catch {
			continue
		}

		let content = original
		for (const dep of frameworkDepNames(pkg)) {
			const version = PACKAGE_VERSIONS[dep]
			if (!version) {
				unknownDeps.add(dep)
				continue
			}
			const to = `^${version}`
			content = content.replace(
				new RegExp(`("${escapeRegExp(dep)}"\\s*:\\s*)"([^"]*)"`, "g"),
				(match, prefix: string, from: string) => {
					if (from === to) return match
					if (LOCAL_RANGE_PREFIXES.some((p) => from.startsWith(p))) return match
					// Le manifeste est figé au build : un package publié APRÈS
					// create-wp-reactor y est encore à sa version précédente. L'écrire
					// reculerait le client — un `update` ne recule jamais.
					const order = compareVersions(from, to)
					if (!Number.isNaN(order) && order > 0) {
						kept.push({ file, dep, from, to })
						return match
					}
					bumps.push({ file, dep, from, to })
					return `${prefix}"${to}"`
				},
			)
		}

		if (content !== original) writeFileSync(file, content)
	}

	return { bumps, kept, unknownDeps: [...unknownDeps] }
}

/**
 * Rafraîchit un repo client existant : re-pousse les fichiers d'INFRA depuis le
 * template embarqué, puis bumpe les deps `@wp-reactor/*` vers les versions
 * embarquées dans ce CLI (d'où `create-wp-reactor@latest` pour les dernières).
 */
export function update(opts: UpdateOptions): UpdateResult {
	const templatesDir = opts.templatesDir ?? TEMPLATES_DIR
	const targetDir = resolve(opts.targetDir)
	const { projectName, namespace } = recoverIdentity(targetDir)
	const tokens = tokensFor(projectName, namespace)
	const written: string[] = []

	const allFeatures = new Set<string>(OPTIONAL_FEATURES)
	for (const rel of INFRA_PATHS) {
		const src = join(templatesDir, rel)
		if (!existsSync(src)) continue
		renderInto(src, join(targetDir, rel), tokens, written, undefined, {
			enabled: allFeatures,
			rel,
		})
	}

	const { bumps, kept, unknownDeps } = bumpFrameworkDeps(targetDir)

	return { targetDir, written, bumps, kept, unknownDeps }
}

export { TEMPLATES_DIR, INFRA_PATHS, PACKAGE_VERSIONS, OPTIONAL_FEATURES }
export type { OptionalFeature } from "./features"

// `--provision` : scaffolding + création de l'infra (Coolify, GitHub, DNS).
export * from "./provision"
